import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { ArrowRight, ChevronRight, Linkedin, Facebook, Twitter, Link as LinkIcon, Mail } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://entrepreneurladyhustler.com';

// Schema markup generator
const generateArticleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.seo_title || article.title,
  "description": article.meta_description,
  "image": article.hero_image,
  "datePublished": article.date_published,
  "dateModified": article.date_modified,
  "author": {
    "@type": "Organization",
    "name": "The Lady Hustler Society Club",
    "url": `${SITE_URL}/en/`
  },
  "publisher": {
    "@type": "Organization",
    "name": "The Lady Hustler Society Club",
    "logo": {
      "@type": "ImageObject",
      "url": `${SITE_URL}/en/logo-lhsc.png`
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `${SITE_URL}/en/blog/${article.slug}/`
  }
});

const generateFAQSchema = (faqItems) => {
  if (!faqItems || faqItems.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
};

const generateBreadcrumbSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": `${SITE_URL}/en/`
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Blog",
      "item": `${SITE_URL}/en/blog/`
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": article.category,
      "item": `${SITE_URL}/en/blog/?category=${article.category_slug}`
    },
    {
      "@type": "ListItem",
      "position": 4,
      "name": article.title,
      "item": `${SITE_URL}/en/blog/${article.slug}/`
    }
  ]
});

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [toc, setToc] = useState([]);

  useEffect(() => {
    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/articles/slug/${slug}`);
      setArticle(response.data);
      
      // Extract table of contents from H2 tags
      const h2Regex = /<h2[^>]*>([^<]+)<\/h2>/gi;
      const matches = [...response.data.content.matchAll(h2Regex)];
      setToc(matches.map((match, index) => ({
        id: `section-${index}`,
        title: match[1]
      })));

      // Fetch related articles
      const relatedRes = await axios.get(`${API_URL}/api/articles/related/${response.data.id}`);
      setRelatedArticles(relatedRes.data);
    } catch (error) {
      console.error('Error fetching article:', error);
      toast.error('Article not found');
    }
    setLoading(false);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed!');
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Subscription failed');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const processContent = (content) => {
    // Add IDs to H2 tags for TOC navigation
    let processed = content;
    let index = 0;
    processed = processed.replace(/<h2([^>]*)>/gi, () => {
      const id = `section-${index}`;
      index++;
      return `<h2 id="${id}">`;
    });
    return processed;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="space-y-4">
            <div className="h-4 w-48 loading-skeleton"></div>
            <div className="h-12 loading-skeleton"></div>
            <div className="h-6 w-3/4 loading-skeleton"></div>
            <div className="h-96 loading-skeleton mt-8"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h1 className="font-heading text-3xl text-[#2C2C2C] mb-4">Article Not Found</h1>
          <p className="font-body text-[#767C91] mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/en/blog" className="cta-button">
            Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent(article.title);
  
  const articleSchema = generateArticleSchema(article);
  const faqSchema = generateFAQSchema(article.faq_items);
  const breadcrumbSchema = generateBreadcrumbSchema(article);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{`${article.seo_title || article.title} | The Lady Hustler Society Club`}</title>
        <meta name="description" content={article.meta_description} />
        <link rel="canonical" href={`${SITE_URL}/en/blog/${article.slug}/`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={article.seo_title || article.title} />
        <meta property="og:description" content={article.meta_description} />
        <meta property="og:image" content={article.hero_image} />
        <meta property="og:url" content={`${SITE_URL}/en/blog/${article.slug}/`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="The Lady Hustler Society Club" />
        <meta property="article:published_time" content={article.date_published} />
        <meta property="article:modified_time" content={article.date_modified} />
        <meta property="article:author" content="The Lady Hustler Society Club" />
        <meta property="article:section" content={article.category} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.seo_title || article.title} />
        <meta name="twitter:description" content={article.meta_description} />
        <meta name="twitter:image" content={article.hero_image} />
        
        {/* Schema.org JSON-LD - Article */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        {/* Schema.org JSON-LD - Breadcrumb */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      
      {/* FAQ Schema - rendered separately to avoid Helmet issues */}
      {faqSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        </Helmet>
      )}
      
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <nav className="breadcrumb flex items-center gap-1 sm:gap-2 text-xs sm:text-sm overflow-x-auto" data-testid="breadcrumb">
            <Link to="/en/blog" className="whitespace-nowrap">Home</Link>
            <ChevronRight className="w-3 h-3 flex-shrink-0" />
            <Link to="/en/blog" className="whitespace-nowrap">Blog</Link>
            <ChevronRight className="w-3 h-3 flex-shrink-0" />
            <Link to={`/en/blog?category=${article.category_slug}`} className="whitespace-nowrap">{article.category}</Link>
            <ChevronRight className="w-3 h-3 flex-shrink-0" />
            <span className="text-[#9D6E6D] truncate max-w-[120px] sm:max-w-xs">{article.title}</span>
          </nav>
        </div>
      </div>

      <article className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Article Header */}
            <header className="mb-8">
              <span className="font-label text-xs text-[#9D6E6D] tracking-widest" data-testid="article-category">
                {article.category}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-[#2C2C2C] mt-3 mb-4 leading-tight" data-testid="article-title">
                {article.title}
              </h1>
              <p className="font-body text-lg text-[#767C91] mb-6" data-testid="article-excerpt">
                {article.excerpt}
              </p>
              
              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-[#767C91] mb-6">
                <span className="font-body">{article.author}</span>
                <span>•</span>
                <span className="font-body">Published {formatDate(article.date_published)}</span>
                {article.date_modified !== article.date_published && (
                  <>
                    <span>•</span>
                    <span className="font-body">Updated {formatDate(article.date_modified)}</span>
                  </>
                )}
                <span>•</span>
                <span className="font-body">{article.read_time} min read</span>
              </div>

              {/* Social Share */}
              <div className="flex items-center gap-3 mb-8" data-testid="social-share">
                <span className="font-label text-xs text-[#767C91] tracking-widest">Share:</span>
                <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                >
                  <Twitter className="w-4 h-4" />
                </a>
                <button 
                  onClick={handleCopyLink}
                  className="w-9 h-9 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                >
                  <LinkIcon className="w-4 h-4" />
                </button>
              </div>

              {/* Hero Image */}
              <figure className="mb-8">
                <img
                  src={article.hero_image}
                  alt={article.hero_image_alt}
                  className="w-full h-auto max-h-[500px] object-cover"
                  loading="eager"
                  fetchpriority="high"
                  width="800"
                  height="500"
                  data-pin-description={`${article.title} - The Lady Hustler Society Club - ${article.primary_keyword}`}
                  data-testid="article-hero-image"
                />
                <figcaption className="font-label text-xs text-[#767C91] mt-2 tracking-widest">
                  The Lady Hustler Society Club
                </figcaption>
              </figure>
            </header>

            {/* Article Content */}
            <div 
              className="article-content max-w-none"
              dangerouslySetInnerHTML={{ __html: processContent(article.content) }}
              data-testid="article-content"
            />

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-[#E2E8F0]" data-testid="article-tags">
                <span className="font-label text-xs text-[#767C91] tracking-widest mb-3 block">Tags:</span>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag, index) => (
                    <span key={index} className="px-3 py-1 bg-[#ABBDDF] text-[#2C2C2C] text-xs font-label">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Author Bio */}
            <div className="mt-8 p-6 bg-[#F2E1E1]" data-testid="author-bio">
              <h3 className="font-heading text-lg text-[#2C2C2C] mb-2">{article.author}</h3>
              <p className="font-body text-[#767C91] text-sm">
                The Lady Hustler Society Club is a global community of women entrepreneurs and professionals building businesses, careers, and impact with resourcefulness, dignity, and collective intelligence.
              </p>
            </div>

            {/* Social Share Footer */}
            <div className="mt-8 py-6 border-t border-b border-[#E2E8F0] flex items-center gap-4">
              <span className="font-label text-xs text-[#767C91] tracking-widest">Share this article:</span>
              <div className="flex gap-2">
                <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                >
                  <Twitter className="w-4 h-4" />
                </a>
                <a 
                  href={`https://pinterest.com/pin/create/button/?url=${shareUrl}&description=${shareTitle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                >
                  <Mail className="w-4 h-4" />
                </a>
                <button 
                  onClick={handleCopyLink}
                  className="w-9 h-9 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                >
                  <LinkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-12" data-testid="related-articles">
                <h2 className="font-heading text-2xl text-[#2C2C2C] mb-6">Related Articles</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedArticles.map((related) => (
                    <Link 
                      key={related.id}
                      to={`/en/blog/${related.slug}`}
                      className="article-card group"
                    >
                      <div className="overflow-hidden">
                        <img 
                          src={related.hero_image}
                          alt={related.hero_image_alt}
                          className="w-full h-40 object-cover"
                          loading="lazy"
                          width="300"
                          height="160"
                        />
                      </div>
                      <div className="p-4">
                        <span className="font-label text-xs text-[#9D6E6D] tracking-widest">
                          {related.category}
                        </span>
                        <h3 className="font-heading text-base text-[#2C2C2C] mt-2 line-clamp-2 group-hover:text-[#9D6E6D] transition-colors">
                          {related.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Newsletter */}
            <div className="mt-12 p-8 bg-[#ABBDDF] text-center" data-testid="article-newsletter">
              <h3 className="font-heading text-2xl text-[#2C2C2C] mb-2">Never Miss a Resource</h3>
              <p className="font-body text-[#2C2C2C]/70 mb-6">
                Join 1,900+ Lady Hustlers receiving practical insights, tools, and stories every week.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-4 py-3 border-0 text-sm"
                  required
                />
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-[#9D6E6D] text-white font-label text-xs tracking-widest"
                >
                  Subscribe
                </button>
              </form>
              <p className="text-xs text-[#2C2C2C]/50 mt-3">No spam. Unsubscribe anytime.</p>
            </div>

            {/* Final CTAs */}
            <div className="mt-12 grid md:grid-cols-2 gap-6">
              <div className="bg-[#F2E1E1] p-6 text-center">
                <h3 className="font-heading text-lg text-[#2C2C2C] mb-2">Become a Lady Hustler</h3>
                <p className="font-body text-[#767C91] text-sm mb-4">
                  Join 1,900+ women building together.
                </p>
                <a href="https://join.entrepreneurladyhustler.com" target="_blank" rel="noopener noreferrer" className="cta-button inline-block">
                  Join Now →
                </a>
              </div>
              <div className="bg-[#ABBDDF] p-6 text-center">
                <h3 className="font-heading text-lg text-[#2C2C2C] mb-2">Activate The Société</h3>
                <p className="font-body text-[#2C2C2C]/70 text-sm mb-4">
                  One system replaces eight tools. $80 CAD/month.
                </p>
                <a href="https://societe.entrepreneurladyhustler.com" target="_blank" rel="noopener noreferrer" className="cta-button inline-block bg-[#2C2C2C]">
                  Learn More →
                </a>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Table of Contents */}
              {toc.length > 0 && (
                <div className="bg-white border border-[#E2E8F0] p-6" data-testid="table-of-contents">
                  <h3 className="font-heading text-lg text-[#2C2C2C] mb-4">In This Article</h3>
                  <nav className="space-y-1">
                    {toc.map((item) => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        className="toc-item block"
                      >
                        {item.title}
                      </a>
                    ))}
                  </nav>
                </div>
              )}

              {/* CTA Card */}
              <div className="sidebar-card">
                <h3 className="font-heading text-lg text-[#2C2C2C] mb-2">Ready to Build With Us?</h3>
                <p className="font-body text-[#767C91] text-sm mb-4">
                  Join 1,900+ Lady Hustlers accessing education, incubators, global networking, and humanitarian impact.
                </p>
                <a href="https://join.entrepreneurladyhustler.com" target="_blank" rel="noopener noreferrer" className="cta-button block text-center">
                  Become a Lady Hustler →
                </a>
              </div>

              {/* Social Share Sticky */}
              <div className="bg-white border border-[#E2E8F0] p-6">
                <h3 className="font-heading text-lg text-[#2C2C2C] mb-4">Share</h3>
                <div className="flex gap-2">
                  <a 
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 h-10 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                  <a 
                    href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 h-10 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                  <a 
                    href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 h-10 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                  >
                    <Twitter className="w-4 h-4" />
                  </a>
                  <button 
                    onClick={handleCopyLink}
                    className="flex-1 h-10 border border-[#E2E8F0] flex items-center justify-center text-[#767C91] hover:text-[#9D6E6D] hover:border-[#9D6E6D] transition-colors"
                  >
                    <LinkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default ArticlePage;
